import { GridFilterItem } from '@mui/x-data-grid-pro';

import { httpClient, HttpMethod } from './http-client';
import { ServiceObject, ServiceSearchData } from '../@types';
import { ActiveInteractionTemplate, InteractionRuleObject } from '../@types/interaction.type';
import {
  GetParameterResponseType,
  InteractionsPartRequestFormInitialPartType,
  InteractionsPartRequestFormPartQty,
  InteractionsPartRequestFormPartResponse,
  queryOptionType,
  TemplateListResponse,
  TemplateRowDataType,
  TriggerPoint,
} from '../@types/interactions.type';
import { InteractionsRulesQueryResponse } from '../@types/interactionsRules.type';

export const getAllInteractionTemplates = (
  queryOptions: queryOptionType
): Promise<TemplateListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;

  let url = `/interactionTemplates?page=${page + 1}&limit=${pageSize}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel?.items?.forEach((filter: GridFilterItem) => {
    url += `&${filter?.field}=${filter?.operator},${filter.value}`;
  });

  return httpClient<TemplateListResponse>(url, HttpMethod.Get);
};

export const createInteractionTemplate = (
  templateData: TemplateRowDataType
): Promise<TemplateRowDataType> => {
  return httpClient<TemplateRowDataType>('/interactionTemplates', HttpMethod.Post, templateData);
};

export const updateInteractionTemplate = (
  id: number,
  updatedTemplateData: TemplateRowDataType
): Promise<TemplateRowDataType> => {
  return httpClient<TemplateRowDataType>(
    `/interactionTemplates/${id}`,
    HttpMethod.Put,
    updatedTemplateData
  );
};

// BFF caps `limit` at 100 (config `pagination.pageLimit`). When the rule
// count exceeds that, walk subsequent pages and merge so the rebuilt list
// page has the full dataset to filter/sort against client-side.
const INTERACTION_RULES_PAGE_SIZE = 100;
// Safety valve so a runaway BFF response can't make us fetch forever.
// 5,000 rules covers every real-world environment with headroom; if we
// hit this we surface a warning so a too-small cap can be diagnosed
// instead of silently dropping rows.
const INTERACTION_RULES_MAX_PAGES = 50;

export const getAllInteractionRules = async (): Promise<InteractionsRulesQueryResponse['data']> => {
  const first = await httpClient<InteractionsRulesQueryResponse>(
    `/interactionRules?limit=${INTERACTION_RULES_PAGE_SIZE}&page=1`,
    HttpMethod.Get
  );
  const collected = [...(first.data ?? [])] as InteractionsRulesQueryResponse['data'];
  const total = first.total ?? collected.length;
  const neededPages = Math.ceil(total / INTERACTION_RULES_PAGE_SIZE);
  const totalPages = Math.min(neededPages, INTERACTION_RULES_MAX_PAGES);
  for (let p = 2; p <= totalPages; p++) {
    const next = await httpClient<InteractionsRulesQueryResponse>(
      `/interactionRules?limit=${INTERACTION_RULES_PAGE_SIZE}&page=${p}`,
      HttpMethod.Get
    );
    if (next.data?.length) collected.push(...next.data);
  }
  if (neededPages > INTERACTION_RULES_MAX_PAGES) {
    console.warn(
      `getAllInteractionRules truncated: total=${total} exceeded cap of ${
        INTERACTION_RULES_PAGE_SIZE * INTERACTION_RULES_MAX_PAGES
      }. Raise INTERACTION_RULES_MAX_PAGES.`
    );
  }
  return collected;
};

export const getInteractionRuleById = (id: number): Promise<InteractionRuleObject> => {
  return httpClient<InteractionRuleObject>(`/interactionRules/${id}`, HttpMethod.Get);
};

export const submitFormsDataWithOriginalPayload = (
  url: string,
  method: HttpMethod,
  payload: unknown
): Promise<unknown> => {
  return httpClient<unknown>(url, method, payload);
};

export const getInteractionsPartRequestFormPartTypes = (
  serviceId: number,
  conditionCode: string
): Promise<InteractionsPartRequestFormInitialPartType[]> => {
  return httpClient<InteractionsPartRequestFormInitialPartType[]>(
    `/partTypes/service/${serviceId}/condition/${conditionCode}`,
    HttpMethod.Get
  );
};

export const getInteractionsPartRequestFormPartTypesChildrenWithParent = (
  partTypeId: number
): Promise<InteractionsPartRequestFormPartResponse> => {
  return httpClient<InteractionsPartRequestFormPartResponse>(
    `/partType/childrenWithParent/${partTypeId}`,
    HttpMethod.Get
  );
};

export const getInteractionsPartRequestFormPartQuantities = (
  partTypeId: number,
  serviceId: number,
  serialised: boolean
): Promise<InteractionsPartRequestFormPartQty> => {
  return httpClient<InteractionsPartRequestFormPartQty>(
    `/getPartQuantitiesBy/partType/${partTypeId}/partCondition/good/service/${serviceId}?isSerialized=${serialised}`,
    HttpMethod.Get
  );
};

export const getAllActiveInteractionTemplates = (): Promise<ActiveInteractionTemplate[]> => {
  return httpClient<ActiveInteractionTemplate[]>('/activeInteractionTemplates', HttpMethod.Get);
};

export const getParameterData = (
  serviceId: number,
  selectedParameter: string,
  mainLocationNodeId: number,
  subLocationNodeId: number,
  clientId: number
): Promise<GetParameterResponseType> => {
  return httpClient<GetParameterResponseType>(
    `/getInteractionParameterData?serviceId=${serviceId}&selectedParameter=${selectedParameter}&mainLocationNodeId=${mainLocationNodeId}&subLocationNodeId=${subLocationNodeId}&clientId=${clientId}`,
    HttpMethod.Get
  );
};

export const getServiceSearchData = (
  serviceId: number,
  serviceSearchData: ServiceSearchData
): Promise<ServiceObject[]> => {
  return httpClient<ServiceObject[]>('/interactions/services/searchData', HttpMethod.Post, {
    serviceId,
    serviceSearchData,
  });
};

export interface NmiCertificateRequest {
  serviceId: number;
  serviceLogId: number;
}

export interface NmiCertificateResponse {
  url?: string;
}

export const downloadNmiCertificate = (
  data: NmiCertificateRequest
): Promise<NmiCertificateResponse> => {
  return httpClient<NmiCertificateResponse>('/interactions/nmi/download', HttpMethod.Post, data);
};

export interface EditInteractionData {
  serviceId: number;
  serviceLogId: number;
  interactionForm: unknown;
}

export interface EditInteractionResponse {
  serviceId: number;
}

export const editInteraction = (data: EditInteractionData): Promise<EditInteractionResponse> => {
  return httpClient<EditInteractionResponse>('/interaction/log', HttpMethod.Put, data);
};

export interface ModifyStructuresToggleActiveData {
  serviceMainLocationNodeId?: number;
  serviceSubLocationNodeId?: number;
  toggleActiveType: string;
}

export const modifyStructuresToggleActive = (
  data: ModifyStructuresToggleActiveData
): Promise<void> => {
  return httpClient<void>('/modifyStructuresToggleActive', HttpMethod.Put, data);
};

export const getTriggerPointTypes = (): Promise<TriggerPoint[]> => {
  return httpClient<TriggerPoint[]>('/triggerPointTypes', HttpMethod.Get);
};
